// AdminPaymentPage.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import API from '../services/api';

// ─── Utilities ───────────────────────────────────────────────────────────────

function getTherapistRowStyle(therapistName = '', colorMap) {
  const payObj = colorMap[therapistName.trim()];
  return { backgroundColor: payObj?.colorCode || 'var(--surface-muted)' };
}

function getPaymentBadgeStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  if (lower.includes('cash')) return { bg: '#16a34a', fg: '#fff' };
  if (lower.includes('card')) return { bg: '#1d4ed8', fg: '#fff' };
  if (lower.includes('check')) return { bg: '#d97706', fg: '#fff' };
  return { bg: 'var(--text-muted)', fg: '#fff' };
}

function formatSessionDate(raw) {
  if (!raw) return '';
  const [y, m, d] = String(raw).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return String(raw);
  return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}`;
}

function getCardTag(companyName = '') {
  const lower = companyName.toLowerCase();
  if (lower.includes('use this') || lower.includes('use for')) return { bg: '#16a34a', text: '#fff' };
  if (lower.includes('hsa')) return { bg: '#2563eb', text: '#fff' };
  if (lower.includes('old')) return { bg: 'var(--text-muted)', text: '#fff' };
  if (lower.includes("don't use") || lower.includes('do not use')) return { bg: 'var(--error-color)', text: '#fff' };
  return null;
}

function dollarFmt(val) {
  return `$${parseFloat(val || 0).toFixed(2)}`;
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const thStyle = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-strong)' };
const tdStyle = { padding: '7px 10px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle' };
const btnPrimary = {
  background: '#2563eb', color: '#fff', border: 'none',
  borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600
};
const btnSecondary = {
  background: 'var(--surface-muted)', color: 'var(--text-strong)', border: '1px solid var(--border-color)',
  borderRadius: 6, padding: '8px 18px', cursor: 'pointer'
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 3 };
const inputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: 6,
  border: '1px solid var(--border-color)', fontSize: 13, boxSizing: 'border-box'
};
const selectStyle = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-color)', fontSize: 13 };
const modalBox = {
  background: '#fff', borderRadius: 12, padding: 24,
  width: '90%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === 'error' ? 'var(--error-color)' : toast.type === 'warning' ? '#d97706' : '#16a34a';
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      background: bg, color: '#fff', padding: '12px 20px',
      borderRadius: 8, maxWidth: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      whiteSpace: 'pre-wrap', lineHeight: 1.4
    }}>
      {toast.message}
    </div>
  );
}

// ─── ModalOverlay ─────────────────────────────────────────────────────────────

function ModalOverlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      {children}
    </div>
  );
}

// ─── CardChip ─────────────────────────────────────────────────────────────────

function fmtAddedDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CardChip({ card, onClick }) {
  const effectiveLabel = card.label || card.companyName || '';
  const tag = getCardTag(effectiveLabel);
  const added = fmtAddedDate(card.addedAt);
  return (
    <div
      onClick={onClick}
      title={`${card.firstName} ${card.lastName} — ${card.maskedCard}`}
      style={{
        display: 'inline-block', border: '1px solid var(--border-color)', borderRadius: 8,
        padding: '6px 10px', margin: '2px', cursor: 'pointer',
        background: '#fff', minWidth: 130,
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 0 2px #3b82f6'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      <div style={{ fontSize: 12, fontWeight: 600 }}>{card.firstName} {card.lastName}</div>
      <div style={{ fontSize: 11, color: '#4b5563' }}>💳 {card.maskedCard}</div>
      {effectiveLabel && (
        tag ? (
          <div style={{
            display: 'inline-block', marginTop: 2,
            background: tag.bg, color: tag.text,
            fontSize: 10, padding: '1px 6px', borderRadius: 4
          }}>{effectiveLabel}</div>
        ) : (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}>
            {effectiveLabel}
          </div>
        )
      )}
      {added && (
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>Added {added}</div>
      )}
    </div>
  );
}

// ─── SessionTable ─────────────────────────────────────────────────────────────

function SessionTable({ title, badge, sessions, clientProfiles, therapistColors, selectedRows, onToggleRow, onToggleAll, onChipClick, onAddProfile, hiddenCards, onEditCards }) {
  const allSelected = sessions.length > 0 && sessions.every(s => selectedRows.has(s.id));
  const someSelected = sessions.some(s => selectedRows.has(s.id));

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
        <span style={{ background: 'var(--border-color)', borderRadius: 12, padding: '1px 8px', fontSize: 12 }}>{badge}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border-color)' }}>
              <th style={{ width: 32, padding: '8px 6px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                  onChange={() => onToggleAll(sessions)}
                />
              </th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>Therapist</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Billed</th>
              <th style={thStyle}>Type</th>
              <th style={{ ...thStyle, maxWidth: 180 }}>Notes</th>
              <th style={thStyle}>Cards (click to charge)</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 16, color: 'var(--text-muted)', textAlign: 'center' }}>No sessions</td></tr>
            )}
            {sessions.map(session => {
              const isSelected = selectedRows.has(session.id);
              const profiles = clientProfiles[session.client_id] || [];
              const billedAmt = parseFloat(session.billed_amount || 0);
              const typeBadge = getPaymentBadgeStyle(session.payment_type);
              return (
                <tr
                  key={session.id}
                  style={{
                    ...getTherapistRowStyle(session.therapist_name, therapistColors),
                    cursor: 'pointer',
                    outline: isSelected ? '2px solid #3b82f6' : 'none',
                    outlineOffset: -2,
                  }}
                  onClick={() => onToggleRow(session.id)}
                >
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected} onChange={() => onToggleRow(session.id)} />
                  </td>
                  <td style={tdStyle}>{session.client_name || `Client #${session.client_id}`}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
                      background: getTherapistRowStyle(session.therapist_name, therapistColors).backgroundColor,
                      border: '1px solid rgba(0,0,0,0.2)', marginRight: 5, verticalAlign: 'middle'
                    }} />
                    {session.therapist_name || '—'}
                  </td>
                  <td style={tdStyle}>{formatSessionDate(session.session_date)}</td>
                  <td style={{
                    ...tdStyle, fontWeight: 700,
                    color: billedAmt === 0 ? 'var(--text-muted)' : billedAmt < 0 ? 'var(--error-color)' : '#111'
                  }}>{dollarFmt(session.billed_amount)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: typeBadge.bg, color: typeBadge.fg,
                      padding: '2px 6px', borderRadius: 4, fontSize: 11
                    }}>
                      {(session.payment_type || '').toUpperCase()}
                    </span>
                  </td>
                  <td
                    style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={session.notes || ''}
                  >
                    {session.notes || ''}
                  </td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    {profiles.length === 0 ? (
                      <button
                        onClick={() => onAddProfile(session.client_id, session.client_name)}
                        style={{
                          border: '1px dashed var(--text-muted)', background: 'transparent',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                          color: 'var(--text-muted)', fontSize: 12,
                        }}
                      >
                        + Add payment profile
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                        {profiles
                          .filter(c => !(hiddenCards[session.client_id] || new Set()).has(c.paymentProfileId))
                          .map(card => (
                            <CardChip
                              key={`${card.customerProfileId}-${card.paymentProfileId}`}
                              card={card}
                              onClick={() => onChipClick(session, card)}
                            />
                          ))
                        }
                        {(hiddenCards[session.client_id] || new Set()).size > 0 && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {(hiddenCards[session.client_id] || new Set()).size} hidden
                          </span>
                        )}
                        <button
                          onClick={() => onEditCards(session.client_id, session.client_name)}
                          style={{ fontSize: 11, padding: '3px 9px', borderRadius: 5, background: 'var(--surface-muted)', border: '1px solid var(--border-color)', color: 'var(--text-strong)', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── QuickChargeModal ─────────────────────────────────────────────────────────

function QuickChargeModal({ session, card, onConfirm, onCancel, onMarkDebt, loading, chargeError }) {
  return (
    <ModalOverlay>
      <div style={modalBox}>
        <h3 style={{ marginTop: 0 }}>Confirm Charge</h3>
        <p><strong>{session.client_name}</strong> — {formatSessionDate(session.session_date)}</p>
        <p style={{ fontSize: 20, fontWeight: 700 }}>{dollarFmt(session.billed_amount)}</p>
        <div style={{ marginBottom: 16 }}>
          <CardChip card={card} onClick={() => {}} />
        </div>
        {chargeError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ color: 'var(--error-color)', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Charge failed: {chargeError}</div>
            <button onClick={onMarkDebt} style={{ background: '#d97706', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Mark as Debt
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnSecondary} disabled={loading}>Cancel</button>
          <button onClick={onConfirm} style={btnPrimary} disabled={loading}>
            {loading ? 'Charging…' : chargeError ? 'Retry Charge' : 'Confirm & Charge'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── CombinedChargeModal ──────────────────────────────────────────────────────

function CombinedChargeModal({ sessions, allCards, onConfirm, onCancel, onMarkDebt, loading, chargeError }) {
  const [selectedCard, setSelectedCard] = useState('');
  const total = sessions.reduce((s, sess) => s + parseFloat(sess.billed_amount || 0), 0);
  const cardKey = c => `${c.customerProfileId}::${c.paymentProfileId}`;
  const parsedCard = allCards.find(c => cardKey(c) === selectedCard);

  return (
    <ModalOverlay>
      <div style={modalBox}>
        <h3 style={{ marginTop: 0 }}>Charge Combined</h3>
        <div style={{ marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
          {sessions.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
              <span>{s.client_name} — {formatSessionDate(s.session_date)}</span>
              <span>{dollarFmt(s.billed_amount)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 4, paddingTop: 4, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
            <span>Total</span><span>{dollarFmt(total)}</span>
          </div>
        </div>
        <label style={labelStyle}>Select card</label>
        <select
          value={selectedCard}
          onChange={e => setSelectedCard(e.target.value)}
          style={{ ...selectStyle, marginTop: 4, marginBottom: 16 }}
        >
          <option value="">— choose a card —</option>
          {allCards.map(c => (
            <option key={cardKey(c)} value={cardKey(c)}>
              {c.firstName} {c.lastName} — {c.maskedCard}{c.companyName ? ` (${c.companyName})` : ''}
            </option>
          ))}
        </select>
        {chargeError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ color: 'var(--error-color)', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Charge failed: {chargeError}</div>
            <button onClick={onMarkDebt} style={{ background: '#d97706', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Mark All as Debt
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnSecondary} disabled={loading}>Cancel</button>
          <button
            onClick={() => onConfirm(parsedCard)}
            style={btnPrimary}
            disabled={loading || !selectedCard}
          >
            {loading ? 'Charging…' : chargeError ? `Retry ${dollarFmt(total)}` : `Confirm & Charge ${dollarFmt(total)}`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── SplitChargeModal ─────────────────────────────────────────────────────────

function SplitChargeModal({ sessions, allCards, onConfirm, onCancel, onMarkDebt, loading, chargeError }) {
  const [card1Key, setCard1Key] = useState('');
  const [card2Key, setCard2Key] = useState('');
  const [pct1, setPct1] = useState(50);
  const total = sessions.reduce((s, sess) => s + parseFloat(sess.billed_amount || 0), 0);
  const pct2 = 100 - pct1;
  const amt1 = Math.round(total * pct1) / 100;
  const amt2 = Math.round((total - amt1) * 100) / 100;
  const cardKey = c => `${c.customerProfileId}::${c.paymentProfileId}`;
  const card1 = allCards.find(c => cardKey(c) === card1Key);
  const card2 = allCards.find(c => cardKey(c) === card2Key);

  return (
    <ModalOverlay>
      <div style={modalBox}>
        <h3 style={{ marginTop: 0 }}>Split Charge</h3>
        <div style={{ marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
          {sessions.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
              <span>{s.client_name} — {formatSessionDate(s.session_date)}</span>
              <span>{dollarFmt(s.billed_amount)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 4, paddingTop: 4, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
            <span>Total</span><span>{dollarFmt(total)}</span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Card 1</label>
            <select value={card1Key} onChange={e => setCard1Key(e.target.value)} style={selectStyle}>
              <option value="">— choose —</option>
              {allCards.map(c => (
                <option key={cardKey(c)} value={cardKey(c)}>
                  {c.firstName} {c.lastName} — {c.maskedCard}{c.companyName ? ` (${c.companyName})` : ''}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <input
                type="number" min={1} max={99} value={pct1}
                onChange={e => setPct1(Math.min(99, Math.max(1, Number(e.target.value))))}
                style={{ width: 60, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border-color)' }}
              />
              <span style={{ fontSize: 12 }}>% = {dollarFmt(amt1)}</span>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Card 2</label>
            <select value={card2Key} onChange={e => setCard2Key(e.target.value)} style={selectStyle}>
              <option value="">— choose —</option>
              {allCards.map(c => (
                <option key={cardKey(c)} value={cardKey(c)}>
                  {c.firstName} {c.lastName} — {c.maskedCard}{c.companyName ? ` (${c.companyName})` : ''}
                </option>
              ))}
            </select>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              {pct2}% = {dollarFmt(amt2)} (remainder)
            </div>
          </div>
        </div>
        {card1Key && card2Key && card1Key === card2Key && (
          <div style={{ color: 'var(--error-color)', fontSize: 12, marginBottom: 8 }}>
            Card 1 and Card 2 must be different cards.
          </div>
        )}
        {chargeError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ color: 'var(--error-color)', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Charge failed: {chargeError}</div>
            <button onClick={onMarkDebt} style={{ background: '#d97706', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Mark All as Debt
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnSecondary} disabled={loading}>Cancel</button>
          <button
            onClick={() => onConfirm({ card1, card2, pct1, pct2 })}
            style={btnPrimary}
            disabled={loading || !card1Key || !card2Key || card1Key === card2Key}
          >
            {loading ? 'Charging…' : chargeError ? 'Retry Both' : 'Confirm & Charge Both'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── ProfileSetupModal ────────────────────────────────────────────────────────

function ProfileSetupModal({ clientId, clientName, onClose, onSuccess }) {
  const [tab, setTab] = useState('create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createForm, setCreateForm] = useState({
    firstName: '', lastName: '', companyName: '', cardNumber: '', expirationDate: '', cardCode: ''
  });
  const [linkProfileId, setLinkProfileId] = useState('');

  const handleCreate = async () => {
    const { cardNumber, expirationDate, cardCode } = createForm;
    if (!cardNumber || !expirationDate || !cardCode) {
      return setError('Card number, expiration, and CVV are required.');
    }
    setLoading(true); setError('');
    try {
      const res = await API.post(`/admin/clients/${clientId}/payment-profile`, createForm);
      if (res.data?.success) { onSuccess(); }
      else { setError(res.data?.message || 'Failed to create profile'); }
    } catch (e) {
      setError(e.response?.data?.message || 'Server error');
    } finally { setLoading(false); }
  };

  const handleLink = async () => {
    if (!linkProfileId.trim()) return setError('Profile ID is required.');
    setLoading(true); setError('');
    try {
      const res = await API.put(`/admin/clients/${clientId}/payment-profile`, {
        existingProfileId: linkProfileId.trim()
      });
      if (res.data?.success) { onSuccess(); }
      else { setError(res.data?.message || 'Failed to link profile'); }
    } catch (e) {
      setError(e.response?.data?.message || 'Server error');
    } finally { setLoading(false); }
  };

  return (
    <ModalOverlay>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Add Payment Profile — {clientName}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ display: 'flex', marginBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
          {[['create', 'Create New Profile'], ['link', 'Link Existing Profile']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: tab === key ? '2px solid #3b82f6' : '2px solid transparent',
                color: tab === key ? '#3b82f6' : 'var(--text-muted)',
                fontWeight: tab === key ? 600 : 400
              }}
            >{label}</button>
          ))}
        </div>
        {error && <div style={{ color: 'var(--error-color)', marginBottom: 10, fontSize: 13 }}>{error}</div>}
        {tab === 'create' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              {[
                ['firstName', 'First Name'], ['lastName', 'Last Name'],
              ].map(([field, label]) => (
                <div key={field}>
                  <label style={labelStyle}>{label}</label>
                  <input style={inputStyle} value={createForm[field]}
                    onChange={e => setCreateForm(f => ({ ...f, [field]: e.target.value }))} />
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Company / Note (e.g. "HSA", "USE THIS", "Mom's card")</label>
                <input style={inputStyle} value={createForm.companyName}
                  onChange={e => setCreateForm(f => ({ ...f, companyName: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Card Number</label>
                <input style={inputStyle} value={createForm.cardNumber}
                  onChange={e => setCreateForm(f => ({ ...f, cardNumber: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Expiration (YYYY-MM)</label>
                <input style={inputStyle} placeholder="2030-12" value={createForm.expirationDate}
                  onChange={e => setCreateForm(f => ({ ...f, expirationDate: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>CVV</label>
                <input style={inputStyle} value={createForm.cardCode}
                  onChange={e => setCreateForm(f => ({ ...f, cardCode: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} style={btnSecondary} disabled={loading}>Cancel</button>
              <button onClick={handleCreate} style={btnPrimary} disabled={loading}>
                {loading ? 'Creating…' : 'Create Profile'}
              </button>
            </div>
          </div>
        )}
        {tab === 'link' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
              Use this if the cardholder already has an Authorize.net profile (e.g. a parent with their own account).
            </p>
            <label style={labelStyle}>Authorize.net Profile ID</label>
            <input
              style={{ ...inputStyle, marginBottom: 16 }}
              value={linkProfileId}
              onChange={e => setLinkProfileId(e.target.value)}
              placeholder="e.g. 123456789"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} style={btnSecondary} disabled={loading}>Cancel</button>
              <button onClick={handleLink} style={btnPrimary} disabled={loading}>
                {loading ? 'Linking…' : 'Link Profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// ─── CardEditModal ────────────────────────────────────────────────────────────

const LABEL_PRESETS = ['Use This', 'HSA', 'Old', "Don't Use"];

function CardLabelEditor({ clientId, card, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(card.label || card.companyName || '');
  const [saving, setSaving] = useState(false);

  const save = async (value) => {
    setSaving(true);
    try {
      await API.put(`/admin/clients/${clientId}/payment-profile/${card.paymentProfileId}/label`, { label: value });
      onSaved?.();
      setOpen(false);
    } catch (_) { /* refresh failure is surfaced elsewhere */ }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => { setText(card.label || card.companyName || ''); setOpen(true); }}
        style={{ marginTop: 4, fontSize: 11, padding: '2px 8px', borderRadius: 5, cursor: 'pointer',
          border: '1px solid var(--border-color)', background: 'var(--surface-muted)', color: 'var(--text-strong)' }}>
        ✏️ Label
      </button>
    );
  }
  return (
    <div style={{ marginTop: 6, padding: 8, border: '1px solid var(--border-color)', borderRadius: 6, background: '#f8fafc' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {LABEL_PRESETS.map((p) => (
          <button key={p} onClick={() => save(p)} disabled={saving}
            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
              border: '1px solid var(--border-color)', background: 'var(--white)', color: 'var(--text-strong)' }}>{p}</button>
        ))}
        <button onClick={() => save('')} disabled={saving}
          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
            border: '1px solid var(--border-color)', background: 'var(--white)', color: 'var(--error-color)' }}>Clear</button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Custom label…"
          style={{ flex: 1, padding: '4px 6px', fontSize: 12, border: '1px solid var(--border-color)', borderRadius: 5 }} />
        <button onClick={() => save(text)} disabled={saving}
          style={{ fontSize: 11, padding: '2px 10px', borderRadius: 5, cursor: 'pointer', border: 'none',
            background: 'var(--primary-color)', color: '#fff', fontWeight: 600 }}>{saving ? '…' : 'Save'}</button>
        <button onClick={() => setOpen(false)} disabled={saving}
          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, cursor: 'pointer',
            border: '1px solid var(--border-color)', background: 'var(--white)', color: 'var(--text-strong)' }}>Cancel</button>
      </div>
    </div>
  );
}

function CardEditModal({ clientId, clientName, profiles, hiddenIds, onToggleHide, onAddCard, onLinkProfile, onSendResult, onClose, addLoading, linkLoading, onLabelSaved }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', companyName: '', cardNumber: '', expirationDate: '', cardCode: '' });
  const [formError, setFormError] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [linkId, setLinkId] = useState('');
  const [linkError, setLinkError] = useState('');
  const [showSend, setShowSend] = useState(false);

  const handleAdd = () => {
    if (!form.cardNumber || !form.expirationDate || !form.cardCode) {
      setFormError('Card number, expiration, and CVV are required.');
      return;
    }
    setFormError('');
    onAddCard(form, () => {
      setForm({ firstName: '', lastName: '', companyName: '', cardNumber: '', expirationDate: '', cardCode: '' });
      setShowForm(false);
    });
  };

  const handleLink = () => {
    if (!linkId.trim()) {
      setLinkError('Authorize.net Profile ID is required.');
      return;
    }
    setLinkError('');
    onLinkProfile(linkId.trim(), () => {
      setLinkId('');
      setShowLink(false);
    });
  };

  return (
    <ModalOverlay>
      <div style={{ ...modalBox, maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Cards — {clientName}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)' }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          {profiles.map(card => {
            const hidden = hiddenIds.has(card.paymentProfileId);
            const effectiveLabel = card.label || card.companyName || '';
            const tag = getCardTag(effectiveLabel);
            const added = fmtAddedDate(card.addedAt);
            return (
              <div key={`${card.customerProfileId}-${card.paymentProfileId}`}
                style={{ padding: '8px 0', borderBottom: '1px solid var(--surface-muted)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, opacity: hidden ? 0.35 : 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{card.firstName} {card.lastName}</div>
                    <div style={{ fontSize: 12, color: '#4b5563' }}>💳 {card.maskedCard}</div>
                    {effectiveLabel && (
                      tag
                        ? <span style={{ display: 'inline-block', marginTop: 2, background: tag.bg, color: tag.text, fontSize: 10, padding: '1px 6px', borderRadius: 4 }}>{effectiveLabel}</span>
                        : <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>{effectiveLabel}</span>
                    )}
                    {added && <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>Added {added}</div>}
                  </div>
                  <button
                    onClick={() => onToggleHide(card.paymentProfileId)}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', border: '1px solid var(--border-color)', background: hidden ? '#f0fdf4' : '#f9fafb', color: hidden ? '#16a34a' : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {hidden ? '👁 Show' : '🙈 Hide'}
                  </button>
                </div>
                <CardLabelEditor clientId={clientId} card={card} onSaved={onLabelSaved} />
              </div>
            );
          })}
        </div>

        {!showForm && !showLink && !showSend && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => { setShowForm(true); setLinkError(''); }} style={{ ...btnSecondary, fontSize: 13, width: '100%', textAlign: 'center' }}>
              + Add another account (new card)
            </button>
            <button onClick={() => { setShowLink(true); setFormError(''); }} style={{ ...btnSecondary, fontSize: 13, width: '100%', textAlign: 'center' }}>
              + Link existing Authorize.net profile ID
            </button>
            <button onClick={() => { setShowSend(true); setFormError(''); setLinkError(''); }} style={{ ...btnSecondary, fontSize: 13, width: '100%', textAlign: 'center' }}>
              ✉ Email client a card-update link
            </button>
          </div>
        )}
        {showSend && (
          <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 14, background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Email a Card-Update Link</div>
              <button onClick={() => setShowSend(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>Cancel</button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0, marginBottom: 10 }}>
              Sends {clientName} a secure link to add a new card themselves. The new card appears here once they submit it.
            </p>
            <SendCardLinkForm
              clientId={clientId}
              onResult={(ok, msg) => { onSendResult?.(ok, msg); if (ok) setShowSend(false); }}
            />
          </div>
        )}
        {showLink && (
          <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 14, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: '#1e293b' }}>Link Existing Profile</div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0, marginBottom: 10 }}>
              Attach an Authorize.net Customer Profile that already exists (e.g. a parent's own account). Its card(s) will appear alongside this client's.
            </p>
            <label style={labelStyle}>Authorize.net Profile ID</label>
            <input
              style={inputStyle}
              value={linkId}
              onChange={e => setLinkId(e.target.value)}
              placeholder="e.g. 123456789"
            />
            {linkError && <div style={{ color: 'var(--error-color)', fontSize: 12, marginTop: 8 }}>{linkError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={() => { setShowLink(false); setLinkError(''); }} style={btnSecondary} disabled={linkLoading}>Cancel</button>
              <button onClick={handleLink} style={btnPrimary} disabled={linkLoading}>{linkLoading ? 'Linking…' : 'Link Profile'}</button>
            </div>
          </div>
        )}
        {showForm && (
          <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 14, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#1e293b' }}>New Account</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {[['firstName', 'First Name'], ['lastName', 'Last Name']].map(([field, label]) => (
                <div key={field}>
                  <label style={labelStyle}>{label}</label>
                  <input style={inputStyle} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Company / Note</label>
                <input style={inputStyle} value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} placeholder='e.g. HSA, "USE THIS", Mom&apos;s card' />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Card Number</label>
                <input style={inputStyle} value={form.cardNumber} onChange={e => setForm(f => ({ ...f, cardNumber: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Expiration (YYYY-MM)</label>
                <input style={inputStyle} placeholder="2030-12" value={form.expirationDate} onChange={e => setForm(f => ({ ...f, expirationDate: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>CVV</label>
                <input style={inputStyle} value={form.cardCode} onChange={e => setForm(f => ({ ...f, cardCode: e.target.value }))} />
              </div>
            </div>
            {formError && <div style={{ color: 'var(--error-color)', fontSize: 12, marginBottom: 8 }}>{formError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowForm(false); setFormError(''); }} style={btnSecondary} disabled={addLoading}>Cancel</button>
              <button onClick={handleAdd} style={btnPrimary} disabled={addLoading}>{addLoading ? 'Adding…' : 'Add Account'}</button>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// ─── SendCardLinkForm ─────────────────────────────────────────────────────────
// Email a client a single-use link to add/update their card. Auto-populates the
// email on file when a client is selected; the admin can edit it and add a note.
function SendCardLinkForm({ clientId, onResult }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    if (!clientId) { setEmail(''); return; }
    API.get(`/admin/clients/${clientId}/billing-email`)
      .then(r => { if (!cancelled && r.data?.success) setEmail(r.data.email || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  const handleSend = async () => {
    if (!clientId) { setError('Select a client first.'); return; }
    if (!email.trim()) { setError('An email address is required.'); return; }
    setLoading(true); setError('');
    try {
      const res = await API.post('/forms/card-update/generate', { clientId, email: email.trim(), message });
      if (res.data?.success) {
        onResult?.(true, `Card-update link sent to ${email.trim()}`);
        setMessage('');
      } else {
        const m = res.data?.message || 'Failed to send link';
        setError(m); onResult?.(false, m);
      }
    } catch (e) {
      const m = e.response?.data?.message || 'Error sending link';
      setError(m); onResult?.(false, m);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <label style={labelStyle}>Client Email</label>
      <input style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} placeholder="client@example.com" />
      <label style={{ ...labelStyle, marginTop: 10 }}>Message (optional)</label>
      <textarea
        style={{ ...inputStyle, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Add a short note to include in the email…"
      />
      {error && <div style={{ color: 'var(--error-color)', fontSize: 12, marginTop: 8 }}>{error}</div>}
      <button onClick={handleSend} style={{ ...btnPrimary, marginTop: 10 }} disabled={loading}>
        {loading ? 'Sending…' : '✉ Send Card-Update Link'}
      </button>
    </div>
  );
}

// ─── AdminPaymentPage ─────────────────────────────────────────────────────────

function AdminPaymentPage() {
  const [unpaidSessions, setUnpaidSessions] = useState([]);
  const [debtSessions, setDebtSessions] = useState([]);
  const [clientProfiles, setClientProfiles] = useState({});
  const [therapistColors, setTherapistColors] = useState({});
  const [clients, setClients] = useState([]);

  const [selectedRows, setSelectedRows] = useState(new Set());

  const [quickCharge, setQuickCharge] = useState(null);
  const [showCombined, setShowCombined] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [profileSetup, setProfileSetup] = useState(null);
  const [chargeLoading, setChargeLoading] = useState(false);

  const [chargeError, setChargeError] = useState(null);

  const [hiddenCards, setHiddenCards] = useState({});      // { [clientId]: Set(paymentProfileId) }
  const [editCardsModal, setEditCardsModal] = useState(null); // { clientId, clientName }
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // Manage Payment Info form state (preserved)
  const [newProfileForm, setNewProfileForm] = useState({
    clientId: '', cardNumber: '', expirationDate: '', cardCode: '',
    firstName: '', lastName: '', companyName: '',
  });
  const [linkProfileClientId, setLinkProfileClientId] = useState('');
  const [existingProfileId, setExistingProfileId] = useState('');
  const [addCardForm, setAddCardForm] = useState({
    clientId: '', cardNumber: '', expirationDate: '', cardCode: '',
    firstName: '', lastName: '', companyName: '',
  });

  useEffect(() => {
    fetchTherapistColors();
    fetchClientsList();
    fetchHiddenCards();
    handleFetchData();
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, []);

  async function fetchHiddenCards() {
    try {
      const res = await API.get('/admin/hidden-cards');
      const map = {};
      (res.data || []).forEach(({ client_id, payment_profile_id }) => {
        if (!map[client_id]) map[client_id] = new Set();
        map[client_id].add(payment_profile_id);
      });
      setHiddenCards(map);
    } catch (err) {
      console.error('Error fetching hidden cards:', err);
    }
  }

  function showToast(message, type = 'success') {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }

  async function fetchTherapistColors() {
    try {
      const res = await API.get('/admin/all-therapists');
      const colorMap = {};
      await Promise.all((res.data || []).map(async t => {
        try {
          const gRes = await API.get(`/admin/therapist-goals/${t.id}`);
          colorMap[`${t.first_name} ${t.last_name}`.trim()] = { colorCode: gRes.data?.color_code || '#FFFFFF' };
        } catch {
          colorMap[`${t.first_name} ${t.last_name}`.trim()] = { colorCode: '#FFFFFF' };
        }
      }));
      setTherapistColors(colorMap);
    } catch (err) {
      console.error('Error fetching therapist colors:', err);
    }
  }

  async function fetchClientsList() {
    try {
      const res = await API.get('/admin/all-clients-all');
      setClients(res.data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }

  async function handleFetchData() {
    try {
      const [unpaidRes, debtRes] = await Promise.all([
        API.get('/admin/daily-report/unpaid'),
        API.get('/admin/daily-report/history?status=DEBT&limit=9999'),
      ]);
      const normalize = rows => rows.map(s => ({
        ...s,
        therapist_name: `${s.therapist_first_name || ''} ${s.therapist_last_name || ''}`.trim() || null,
      }));
      const uSessions = normalize(unpaidRes.data || []);
      const dCard = normalize((debtRes.data || []).filter(s => (s.payment_type || '').toLowerCase() === 'card'));

      setUnpaidSessions(uSessions);
      setDebtSessions(dCard);
      setSelectedRows(new Set());

      const uniqueClientIds = new Set([
        ...uSessions.map(s => s.client_id).filter(Boolean),
        ...dCard.map(s => s.client_id).filter(Boolean),
      ]);
      const profileDict = {};
      await Promise.all([...uniqueClientIds].map(async cId => {
        try {
          const prRes = await API.get(`/admin/clients/${cId}/payment-profiles`);
          profileDict[cId] = prRes.data?.success ? prRes.data.profiles : [];
        } catch {
          profileDict[cId] = [];
        }
      }));
      setClientProfiles(profileDict);
    } catch (err) {
      console.error('Error fetching data:', err);
      showToast('Error loading sessions', 'error');
    }
  }

  async function refreshClientProfiles(clientId) {
    try {
      const prRes = await API.get(`/admin/clients/${clientId}/payment-profiles`);
      setClientProfiles(prev => ({
        ...prev,
        [clientId]: prRes.data?.success ? prRes.data.profiles : []
      }));
    } catch (err) {
      console.error('Error refreshing profiles:', err);
    }
  }

  function toggleRow(id) {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll(sessions) {
    const allSel = sessions.every(s => selectedRows.has(s.id));
    setSelectedRows(prev => {
      const next = new Set(prev);
      sessions.forEach(s => allSel ? next.delete(s.id) : next.add(s.id));
      return next;
    });
  }

  const allSessions = useMemo(() => [...unpaidSessions, ...debtSessions], [unpaidSessions, debtSessions]);

  const selectedSessions = useMemo(
    () => allSessions.filter(s => selectedRows.has(s.id)),
    [allSessions, selectedRows]
  );

  const selectedTotal = useMemo(
    () => selectedSessions.reduce((sum, s) => sum + parseFloat(s.billed_amount || 0), 0),
    [selectedSessions]
  );

  const allCardsForSelected = useMemo(() => {
    const seen = new Set();
    const cards = [];
    for (const s of selectedSessions) {
      for (const card of (clientProfiles[s.client_id] || [])) {
        const key = `${card.customerProfileId}::${card.paymentProfileId}`;
        if (!seen.has(key)) { seen.add(key); cards.push(card); }
      }
    }
    return cards;
  }, [selectedSessions, clientProfiles]);

  async function handleQuickCharge() {
    if (!quickCharge) return;
    const { session, card } = quickCharge;
    setChargeLoading(true);
    setChargeError(null);
    try {
      const res = await API.post(`/admin/clients/${session.client_id}/charge`, {
        amount: session.billed_amount,
        customerProfileId: card.customerProfileId,
        paymentProfileId: card.paymentProfileId,
        sessionId: session.id,
      });
      if (res.data?.success) {
        showToast(`Charged ${dollarFmt(session.billed_amount)} — TxnID: ${res.data.transactionId}`);
        setQuickCharge(null);
        handleFetchData();
      } else {
        setChargeError(res.data?.message || 'Charge failed');
      }
    } catch (err) {
      setChargeError(err.response?.data?.message || 'Server error');
    } finally {
      setChargeLoading(false);
    }
  }

  async function handleCombinedCharge(card) {
    if (!card) return;
    setChargeLoading(true);
    setChargeError(null);
    try {
      const res = await API.post('/admin/charge-combined', {
        sessionIds: selectedSessions.map(s => s.id),
        customerProfileId: card.customerProfileId,
        paymentProfileId: card.paymentProfileId,
      });
      if (res.data?.success) {
        showToast(`Charged ${dollarFmt(selectedTotal)} — TxnID: ${res.data.transactionId}`);
        setShowCombined(false);
        handleFetchData();
      } else {
        setChargeError(res.data?.message || 'Charge failed');
      }
    } catch (err) {
      setChargeError(err.response?.data?.message || 'Server error');
    } finally {
      setChargeLoading(false);
    }
  }

  async function handleSplitCharge({ card1, card2, pct1, pct2 }) {
    if (!card1 || !card2) return;
    setChargeLoading(true);
    setChargeError(null);
    try {
      const res = await API.post('/admin/charge-split', {
        sessionIds: selectedSessions.map(s => s.id),
        card1: { customerProfileId: card1.customerProfileId, paymentProfileId: card1.paymentProfileId, percentage: pct1 },
        card2: { customerProfileId: card2.customerProfileId, paymentProfileId: card2.paymentProfileId, percentage: pct2 },
      });
      if (res.data?.success) {
        showToast(`Split charge complete — TxnID1: ${res.data.transactionId1}, TxnID2: ${res.data.transactionId2}`);
        setShowSplit(false);
        handleFetchData();
      } else {
        setChargeError(res.data?.message || 'Charge failed');
      }
    } catch (err) {
      const d = err.response?.data;
      if (d?.partial) {
        showToast(`Card 1 charged (TxnID: ${d.transactionId1}) but Card 2 FAILED.\n${d.message}`, 'warning');
        setShowSplit(false);
        handleFetchData();
      } else {
        setChargeError(d?.message || 'Server error');
      }
    } finally {
      setChargeLoading(false);
    }
  }

  async function handleMarkDebt(sessionIds) {
    if (!sessionIds?.length) return;
    const label = sessionIds.length === 1 ? '1 session' : `${sessionIds.length} sessions`;
    if (!window.confirm(`Mark ${label} as debt? This will notify the assigned therapist(s).`)) return;
    try {
      await API.put('/admin/daily-report', {
        updates: sessionIds.map(id => ({ sessionId: id, status: 'DEBT' })),
      });
      showToast(`${label} marked as debt`);
      setQuickCharge(null);
      setShowCombined(false);
      setShowSplit(false);
      setChargeError(null);
      handleFetchData();
    } catch (err) {
      showToast('Failed to mark as debt', 'error');
    }
  }

  const toggleHideCard = (clientId, paymentProfileId) => {
    const willHide = !(hiddenCards[clientId] || new Set()).has(paymentProfileId);
    setHiddenCards(prev => {
      const set = new Set(prev[clientId] || []);
      willHide ? set.add(paymentProfileId) : set.delete(paymentProfileId);
      return { ...prev, [clientId]: set };
    });
    // Persist so hidden cards stay hidden next time the client is loaded.
    API.post(`/admin/clients/${clientId}/payment-profile/hide`, { paymentProfileId, hidden: willHide })
      .catch(err => console.error('Error saving hidden card:', err));
  };

  const handleAddCardFromModal = async (form, onDone) => {
    if (!editCardsModal) return;
    const clientId = editCardsModal.clientId;
    setAddCardLoading(true);
    try {
      const res = await API.post(`/admin/clients/${clientId}/payment-profile`, {
        cardNumber: form.cardNumber, expirationDate: form.expirationDate, cardCode: form.cardCode,
        firstName: form.firstName, lastName: form.lastName, companyName: form.companyName,
      });
      if (res.data?.success) {
        showToast('Account added!');
        await refreshClientProfiles(clientId);
        onDone();
      } else {
        showToast(res.data?.message || 'Failed to add account', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.message || 'Error adding account', 'error');
    } finally {
      setAddCardLoading(false);
    }
  };

  const handleLinkProfileFromModal = async (existingProfileId, onDone) => {
    if (!editCardsModal) return;
    const clientId = editCardsModal.clientId;
    setLinkLoading(true);
    try {
      const res = await API.put(`/admin/clients/${clientId}/payment-profile`, { existingProfileId });
      if (res.data?.success) {
        showToast('Profile linked!');
        await refreshClientProfiles(clientId);
        onDone();
      } else {
        showToast(res.data?.message || 'Failed to link profile', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.message || 'Error linking profile', 'error');
    } finally {
      setLinkLoading(false);
    }
  };

  // Manage Payment Info handlers (preserved, alert replaced with toast)
  const handleCreatePaymentProfile = async () => {
    const f = newProfileForm;
    if (!f.clientId || !f.cardNumber || !f.expirationDate || !f.cardCode) {
      return showToast('Please fill in required fields (client, card #, exp, CVV).', 'error');
    }
    try {
      const res = await API.post(`/admin/clients/${f.clientId}/payment-profile`, {
        cardNumber: f.cardNumber, expirationDate: f.expirationDate, cardCode: f.cardCode,
        firstName: f.firstName, lastName: f.lastName, companyName: f.companyName,
      });
      if (res.data?.success) { showToast(`Profile created! ID: ${res.data.profileId}`); }
      else { showToast('Failed to create profile', 'error'); }
    } catch { showToast('Error creating profile', 'error'); }
  };

  const handleLinkExistingProfile = async () => {
    if (!linkProfileClientId || !existingProfileId) {
      return showToast('Select a client and enter the existing profile ID.', 'error');
    }
    try {
      const res = await API.put(`/admin/clients/${linkProfileClientId}/payment-profile`, { existingProfileId });
      if (res.data?.success) { showToast('Existing profile linked successfully!'); }
      else { showToast(res.data?.message || 'Failed to link profile', 'error'); }
    } catch { showToast('Error linking profile', 'error'); }
  };

  const handleAddCardToExistingProfile = async () => {
    const f = addCardForm;
    if (!f.clientId || !f.cardNumber || !f.expirationDate || !f.cardCode) {
      return showToast('Please fill in required fields (client, card #, exp, CVV).', 'error');
    }
    try {
      const res = await API.post(`/admin/clients/${f.clientId}/payment-profile/add-card`, {
        cardNumber: f.cardNumber, expirationDate: f.expirationDate, cardCode: f.cardCode,
        firstName: f.firstName, lastName: f.lastName, companyName: f.companyName,
      });
      if (res.data?.success) { showToast('Card added to profile!'); }
      else { showToast('Failed to add card', 'error'); }
    } catch { showToast('Error adding card', 'error'); }
  };

  return (
    <div style={{ padding: '20px 24px', fontFamily: 'sans-serif', maxWidth: 1400, margin: '0 auto', paddingBottom: 80 }}>
      <Toast toast={toast} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Payment Collection</h2>
        <button onClick={handleFetchData} style={{ ...btnSecondary, fontSize: 13 }}>↺ Refresh</button>
      </div>

      <SessionTable
        title="Unpaid Sessions"
        badge={`${unpaidSessions.length} sessions`}
        sessions={unpaidSessions}
        clientProfiles={clientProfiles}
        therapistColors={therapistColors}
        selectedRows={selectedRows}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        onChipClick={(session, card) => setQuickCharge({ session, card })}
        onAddProfile={(clientId, clientName) => setProfileSetup({ clientId, clientName })}
        hiddenCards={hiddenCards}
        onEditCards={(clientId, clientName) => setEditCardsModal({ clientId, clientName })}
      />

      <SessionTable
        title="Debt Sessions"
        badge={`${debtSessions.length} open`}
        sessions={debtSessions}
        clientProfiles={clientProfiles}
        therapistColors={therapistColors}
        selectedRows={selectedRows}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        onChipClick={(session, card) => setQuickCharge({ session, card })}
        onAddProfile={(clientId, clientName) => setProfileSetup({ clientId, clientName })}
        hiddenCards={hiddenCards}
        onEditCards={(clientId, clientName) => setEditCardsModal({ clientId, clientName })}
      />

      {/* Sticky action bar */}
      {selectedRows.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#1e293b', color: '#fff', padding: '12px 24px',
          display: 'flex', alignItems: 'center', gap: 12, zIndex: 500,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.2)'
        }}>
          <span style={{ flex: 1, fontWeight: 600 }}>
            {selectedRows.size} selected · {dollarFmt(selectedTotal)}
          </span>
          <button onClick={() => { setChargeError(null); setShowCombined(true); }} style={{ ...btnPrimary, background: '#16a34a' }}>
            Charge Combined
          </button>
          <button onClick={() => { setChargeError(null); setShowSplit(true); }} style={{ ...btnPrimary, background: '#7c3aed' }}>
            Split Charge
          </button>
          <button
            onClick={() => {
              const toMark = selectedSessions.filter(s => !debtSessions.find(d => d.id === s.id));
              handleMarkDebt(toMark.map(s => s.id));
            }}
            style={{ ...btnPrimary, background: '#d97706' }}
          >
            Mark Debt
          </button>
          <button onClick={() => setSelectedRows(new Set())} style={btnSecondary}>Clear</button>
        </div>
      )}

      {/* Manage Payment Info — preserved from original */}
      <div style={{ marginTop: 40, borderTop: '2px solid var(--border-color)', paddingTop: 24 }}>
        <h3>Manage Payment Info</h3>

        <div style={{ marginBottom: 24, border: '1px solid var(--border-color)', borderRadius: 8, padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Create New Authorize.net Profile</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Client</label>
              <select value={newProfileForm.clientId} onChange={e => setNewProfileForm(f => ({ ...f, clientId: e.target.value }))} style={selectStyle}>
                <option value="">— select —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            {[['firstName','First Name'],['lastName','Last Name'],['companyName','Company/Note'],['cardNumber','Card Number'],['expirationDate','Exp (YYYY-MM)'],['cardCode','CVV']].map(([field, label]) => (
              <div key={field}>
                <label style={labelStyle}>{label}</label>
                <input style={inputStyle} value={newProfileForm[field]} onChange={e => setNewProfileForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
            <button onClick={handleCreatePaymentProfile} style={btnPrimary}>Create Profile</button>
          </div>
        </div>

        <div style={{ marginBottom: 24, border: '1px solid var(--border-color)', borderRadius: 8, padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Link Existing Authorize.net Profile</h4>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Client</label>
              <select value={linkProfileClientId} onChange={e => setLinkProfileClientId(e.target.value)} style={selectStyle}>
                <option value="">— select —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Authorize.net Profile ID</label>
              <input style={inputStyle} value={existingProfileId} onChange={e => setExistingProfileId(e.target.value)} />
            </div>
            <button onClick={handleLinkExistingProfile} style={btnPrimary}>Link Profile</button>
          </div>
        </div>

        <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Add Card to Existing Profile</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Client</label>
              <select value={addCardForm.clientId} onChange={e => setAddCardForm(f => ({ ...f, clientId: e.target.value }))} style={selectStyle}>
                <option value="">— select —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            {[['firstName','First Name'],['lastName','Last Name'],['companyName','Company/Note'],['cardNumber','Card Number'],['expirationDate','Exp (YYYY-MM)'],['cardCode','CVV']].map(([field, label]) => (
              <div key={field}>
                <label style={labelStyle}>{label}</label>
                <input style={inputStyle} value={addCardForm[field]} onChange={e => setAddCardForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
            <button onClick={handleAddCardToExistingProfile} style={btnPrimary}>Add Card</button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {quickCharge && (
        <QuickChargeModal
          session={quickCharge.session}
          card={quickCharge.card}
          onConfirm={handleQuickCharge}
          onCancel={() => { setQuickCharge(null); setChargeError(null); }}
          onMarkDebt={() => handleMarkDebt([quickCharge.session.id])}
          loading={chargeLoading}
          chargeError={chargeError}
        />
      )}
      {showCombined && (
        <CombinedChargeModal
          sessions={selectedSessions}
          allCards={allCardsForSelected}
          onConfirm={handleCombinedCharge}
          onCancel={() => { setShowCombined(false); setChargeError(null); }}
          onMarkDebt={() => handleMarkDebt(selectedSessions.filter(s => !debtSessions.find(d => d.id === s.id)).map(s => s.id))}
          loading={chargeLoading}
          chargeError={chargeError}
        />
      )}
      {showSplit && (
        <SplitChargeModal
          sessions={selectedSessions}
          allCards={allCardsForSelected}
          onConfirm={handleSplitCharge}
          onCancel={() => { setShowSplit(false); setChargeError(null); }}
          onMarkDebt={() => handleMarkDebt(selectedSessions.filter(s => !debtSessions.find(d => d.id === s.id)).map(s => s.id))}
          loading={chargeLoading}
          chargeError={chargeError}
        />
      )}
      {profileSetup && (
        <ProfileSetupModal
          clientId={profileSetup.clientId}
          clientName={profileSetup.clientName}
          onClose={() => setProfileSetup(null)}
          onSuccess={() => {
            const cid = profileSetup.clientId;
            setProfileSetup(null);
            refreshClientProfiles(cid);
          }}
        />
      )}
      {editCardsModal && (
        <CardEditModal
          clientId={editCardsModal.clientId}
          clientName={editCardsModal.clientName}
          profiles={clientProfiles[editCardsModal.clientId] || []}
          hiddenIds={hiddenCards[editCardsModal.clientId] || new Set()}
          onToggleHide={paymentProfileId => toggleHideCard(editCardsModal.clientId, paymentProfileId)}
          onAddCard={handleAddCardFromModal}
          onLinkProfile={handleLinkProfileFromModal}
          onSendResult={(ok, msg) => showToast(msg, ok ? 'success' : 'error')}
          onClose={() => setEditCardsModal(null)}
          addLoading={addCardLoading}
          linkLoading={linkLoading}
          onLabelSaved={() => refreshClientProfiles(editCardsModal.clientId)}
        />
      )}
    </div>
  );
}

export default AdminPaymentPage;
